import { getUrlParameter } from "../../DonateForm/Components/DonateForm";
import { sanitize } from "dompurify";
import { Field, Formik, FormikErrors } from "formik";
import React from "react";
import { Button, Modal } from "react-bootstrap";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import LoadingOverlay from "react-loading-overlay";
import { ClipLoader } from "react-spinners";
import { injectStripe, ReactStripeElements } from "react-stripe-elements";
import { formatCurrency, isEmpty, isNullOrWhiteSpace } from "../../../common/helper/helper";
import FormField from "../../DonateForm/Components/FormField";
import MaskedFormField from "../../DonateForm/Components/MaskedFormField";
import StripeCheckout from "../../DonateForm/Components/StripeCheckout";
import "../../DonateForm/Style/DonateForm.scss";
import { States } from "../../GenericForm/Components/Constants";
import { getRcTokenAsync, IReCaptCha, tokenFailable } from "../../GoogleReCaptcha/Recaptcha";
import { IFormSelectOption } from "../../WaMembershipForm/Components/Constants";
import "../Style/actMembershipForm.scss";
import {
	ACTMembershipFormConstants,
	ACTMembershipInitialValues,
	Ethnicities,
	Languages,
} from "./constants";
import { ACTMembershipValidationSchema } from "./FunctionLib";

export interface IACTMembershipFormProps extends IReCaptCha {
	stripe: ReactStripeElements.StripeProps;
	contentId: string;
	publishableKey: string;
	unionList: IFormSelectOption[];
	yearlyPaymentAmount: number;
	concessionCardDiscountAmount: number;
	unionDiscountAmount: number;
	redirectUrl?: string;
	redirectTarget?: string;
}

interface IPaymentResponse {
	Message: string;
	ReceiptNumber: string;
	Success: boolean;
}

const categoryMap = {
	under18: "under 18 years of age",
	conces: "government pension or payment card",
	unemp: "unemployed",
	retrd: "retired from work",
	student: "study full-time",
	casual: "casually employed",
	norfolk: "enrolled in Norfolk Island",
	os: "on an overseas posting or mission",
	union: "union member",
	pt: "part-time worker",
	gen: "none of the above or full-time worker"
};

const FormValidator = (props: { validateForm: (values?: any) => Promise<FormikErrors<any>> }) => {
	React.useEffect(() => {
		props.validateForm();
	}, []);

	return <></>;
};
function ACTMembershipForm(props: IACTMembershipFormProps) {
	const { executeRecaptcha } = useGoogleReCaptcha();

	const touchFields = (
		setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void
	) => {
		Object.entries(ACTMembershipFormConstants).forEach(([key, value]) =>
			setFieldTouched(ACTMembershipFormConstants[key], true, true)
		);
	};

	const continueClickHandler = (
		errors: FormikErrors<IACTMembershipFormProps>,
		setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void
	) => {
		if (
			!Object.entries(ACTMembershipFormConstants).some(
				([key, value]) => errors[ACTMembershipFormConstants[key]]
			)
		) {
			return true;
		} else {
			touchFields(setFieldTouched);
			return false;
		}
	};

	const submitFunction = async (values: any) => {
		const rcToken = getRcTokenAsync("actMembership", executeRecaptcha);
		setSubmitting(true);
		setModalVisible(false);

		const { amount, interval } = getCategoryDetails(
			values[ACTMembershipFormConstants.category]
		);

		let fixedValues: Partial<IACTMembershipFormProps> = {};

		fixedValues[ACTMembershipFormConstants.membershipFee] = amount;
		fixedValues[ACTMembershipFormConstants.transferring] =
			values[ACTMembershipFormConstants.transferring];
		fixedValues[ACTMembershipFormConstants.declareContactShared] =
			values[ACTMembershipFormConstants.declareContactShared] === true ? "yes" : "no";
		fixedValues[ACTMembershipFormConstants.declareNotPoliticalMember] =
			values[ACTMembershipFormConstants.declareNotPoliticalMember] === true ? "yes" : "no";
		fixedValues[ACTMembershipFormConstants.declareJoinAlp] =
			values[ACTMembershipFormConstants.declareJoinAlp] === true ? "yes" : "no";
		fixedValues[ACTMembershipFormConstants.declarePayAlp] =
			values[ACTMembershipFormConstants.declarePayAlp] === true ? "yes" : "no";
		fixedValues[ACTMembershipFormConstants.isUnionMember] =
			values[ACTMembershipFormConstants.isUnionMember] === true ? "yes" : "no";
		fixedValues[ACTMembershipFormConstants.aboriginalDescent] =
			values[ACTMembershipFormConstants.aboriginalDescent];
		fixedValues[ACTMembershipFormConstants.LBGTIQ] = values[ACTMembershipFormConstants.LBGTIQ];
		fixedValues[ACTMembershipFormConstants.CALD] = values[ACTMembershipFormConstants.CALD];
		fixedValues[ACTMembershipFormConstants.hasDisability] = values[ACTMembershipFormConstants.hasDisability];
		fixedValues[ACTMembershipFormConstants.sameAsResidential] =
			values[ACTMembershipFormConstants.sameAsResidential];
		fixedValues[ACTMembershipFormConstants.paymentInterval] = interval;
		Object.entries(values).forEach(([key, value]) => {
			if (
				key !== ACTMembershipFormConstants.transferring &&
				key !== ACTMembershipFormConstants.membershipFee &&
				key !== ACTMembershipFormConstants.declareContactShared &&
				key !== ACTMembershipFormConstants.declareNotPoliticalMember &&
				key !== ACTMembershipFormConstants.declareJoinAlp &&
				key !== ACTMembershipFormConstants.declarePayAlp &&
				key !== ACTMembershipFormConstants.sameAsResidential &&
				key !== ACTMembershipFormConstants.isUnionMember &&
				key !== ACTMembershipFormConstants.aboriginalDescent &&
				key !== ACTMembershipFormConstants.LBGTIQ &&
				key !== ACTMembershipFormConstants.CALD &&
				key !== ACTMembershipFormConstants.paymentInterval &&
				key !== ACTMembershipFormConstants.hasDisability
			) {
				fixedValues[key] = sanitize(values[key].trim(), { ALLOWED_TAGS: [] });
			}
		});
		const GetToken = async () => {
			try {
				return await props.stripe.createToken({
					name: `${values[ACTMembershipFormConstants.firstName]} ${
						values[ACTMembershipFormConstants.lastName]
					}`,
				});
			} catch (error) {
				setSubmitting(false);
				setSubmitError("Payment Declined");
				setIsLoading(false);
			}
		};

		const { token, error } = await GetToken();

		const ContentId = parseInt(props.contentId, 10);

		if (error != null && !isEmpty(error)) {
			setSubmitting(false);
			setIsLoading(false);
			if (error.message) {
				setSubmitError(error.message);
			} else if (error.decline_code) {
				setSubmitError(error.decline_code);
			} else {
				setSubmitError("Generating token failed and please try again later.");
			}
		} else if (!token || !token.id) {
			setSubmitError("Generating token failed and please try again later.");
		} else {
			const body = {
				...fixedValues,
				ContentId: ContentId,
				SecureToken: token.id,
				RcToken: await tokenFailable(rcToken),
			};
			setIsLoading(true);
			fetch("/Umbraco/Api/ACTMembership/Submit", {
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify(body),
			})
				.then(async (resp) => {
					if (!resp.ok) {
						setSubmitError(
							"Sorry an unexpected error occurred, your payment MAY have been taken so please contact us."
						);
						setSubmitting(false);
						setIsLoading(false);
					} else {
						try {
							var data = (await resp.json()) as IPaymentResponse;
							if (!data.Success) {
								setSubmitError(data.Message);
								setSubmitting(false);
								setIsLoading(false);
							} else {
								if (submitError) {
									setSubmitError("");
								}
								setSuccess(true);
								setIsLoading(false);
								if (!isNullOrWhiteSpace(props.redirectUrl)) {
									if (props.redirectTarget == "_blank") {
										setTimeout(
											() =>
												window.open(
													props.redirectUrl,
													props.redirectTarget
												),
											5000
										);
									} else {
										setTimeout(
											() => (window.location.href = props.redirectUrl),
											5000
										);
									}
								}
							}
						} catch (e) {
							setSubmitError(
								"An unexpected error occured, please contact us for support"
							);
							setSubmitting(false);
							setIsLoading(false);
						}
					}
				})
				.catch((error) => {
					setSubmitError(error);
					setSubmitting(false);
					setIsLoading(false);
				});
		}
	};

	const getCategoryDetails = (category: string) => {
		if (
			category === "under18" ||
			category === "conces" ||
			category === "unemp" ||
			category === "retrd" ||
			category === "student" ||
			category === "casual" ||
			category === "norfolk" ||
			category === "os"
		) {
			return { amount: props.concessionCardDiscountAmount, interval: "Year" };
		} else if (category === "union" || category === "pt") {
			return { amount: props.unionDiscountAmount, interval: "Month" };
		} else if (category === "gen") {
			return { amount: props.yearlyPaymentAmount, interval: "Month" };
		}
	};

	const [stripeComplete, setStripeComplete] = React.useState<boolean>(false);
	const [submitting, setSubmitting] = React.useState(false);
	const [submitError, setSubmitError] = React.useState<string>("");
	const [isLoading, setIsLoading] = React.useState(false);
	const [modalVisible, setModalVisible] = React.useState<boolean>(false);
	const [success, setSuccess] = React.useState<boolean>(false);

	const FinalACTMembershipInitialValues = ACTMembershipInitialValues;

	FinalACTMembershipInitialValues[ACTMembershipFormConstants.firstName] =
		getUrlParameter("fn") ?? ACTMembershipInitialValues[ACTMembershipFormConstants.firstName];
	FinalACTMembershipInitialValues[ACTMembershipFormConstants.lastName] =
		getUrlParameter("ln") ?? ACTMembershipInitialValues[ACTMembershipFormConstants.lastName];
	FinalACTMembershipInitialValues[ACTMembershipFormConstants.email] =
		getUrlParameter("em") ?? ACTMembershipInitialValues[ACTMembershipFormConstants.email];
	FinalACTMembershipInitialValues[ACTMembershipFormConstants.mobile] =
		getUrlParameter("mb") ?? ACTMembershipInitialValues[ACTMembershipFormConstants.mobile];
	FinalACTMembershipInitialValues[ACTMembershipFormConstants.postcode] =
		getUrlParameter("pc") ?? ACTMembershipInitialValues[ACTMembershipFormConstants.postcode];
	FinalACTMembershipInitialValues[ACTMembershipFormConstants.address1] =
		getUrlParameter("ad") ?? ACTMembershipInitialValues[ACTMembershipFormConstants.address1];
	FinalACTMembershipInitialValues[ACTMembershipFormConstants.suburb] =
		getUrlParameter("su") ?? ACTMembershipInitialValues[ACTMembershipFormConstants.suburb];

	return (
		<Formik
			initialValues={ACTMembershipInitialValues}
			validationSchema={ACTMembershipValidationSchema}
			onSubmit={submitFunction}
			render={({
				errors,
				touched,
				values,
				setFieldValue,
				setFieldTouched,
				setTouched,
				validateForm,
			}) => (
				<>
					<LoadingOverlay
						active={isLoading}
						className={"loadingOverlay"}
						spinner={<ClipLoader sizeUnit={"px"} size={200} />}
					>
						<div className={"donForm col-md-6"}>
							<FormValidator validateForm={validateForm} />
							{!submitting && (
								<>
									<h1 className="donFormHeader label">1.Personal Details</h1>
									<div className="fieldGrid">
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												First Name*
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.firstName]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.firstName}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants.firstName
													] &&
														errors[
															ACTMembershipFormConstants.firstName
														]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Last Name*
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.lastName]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.lastName}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.lastName] &&
														errors[ACTMembershipFormConstants.lastName]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Preferred First Name
											</label>
											<div
												className={`${
													errors[
														ACTMembershipFormConstants
															.preferredFirstName
													]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={
														ACTMembershipFormConstants.preferredFirstName
													}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants
															.preferredFirstName
													] &&
														errors[
															ACTMembershipFormConstants
																.preferredFirstName
														]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Title
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.title]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.title}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.title] &&
														errors[ACTMembershipFormConstants.title]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Date Of Birth*
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.dateOfBirth]
														? "error"
														: ""
												}`}
											>
												<MaskedFormField
													name={ACTMembershipFormConstants.dateOfBirth}
													placeholder=""
													errors={errors}
													touched={touched}
													colorTheme="red"
													isColumn={true}
													setFieldValue={setFieldValue}
													values={values}
													mask={[
														/\d/,
														/\d/,
														"/",
														/\d/,
														/\d/,
														"/",
														/\d/,
														/\d/,
														/\d/,
														/\d/,
													]}
													setTouched={setTouched}
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants.dateOfBirth
													] &&
														errors[
															ACTMembershipFormConstants.dateOfBirth
														]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Email*
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.email]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.email}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.email] &&
														errors[ACTMembershipFormConstants.email]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Mobile
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.mobile]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.mobile}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.mobile] &&
														errors[ACTMembershipFormConstants.mobile]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Home Phone
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.phoneHome]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.phoneHome}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants.phoneHome
													] &&
														errors[
															ACTMembershipFormConstants.phoneHome
														]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Work Phone
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.phoneBusiness]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.phoneBusiness}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants.phoneBusiness
													] &&
														errors[
															ACTMembershipFormConstants.phoneBusiness
														]}
												</div>
											</div>
										</div>
									</div>
									<div className="fieldGrid">
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Address 1
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.address1]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.address1}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.address1] &&
														errors[ACTMembershipFormConstants.address1]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Address 2
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.address2]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.address2}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.address2] &&
														errors[ACTMembershipFormConstants.address2]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Postcode
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.postcode]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.postcode}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.postcode] &&
														errors[ACTMembershipFormConstants.postcode]}
												</div>
											</div>
										</div>
										<div className="textStyle">
											<label className="formCheckboxLabel label col-md-12 my-2 px-4">
												State
											</label>
											<div
												className={` col-md-12 my-1 px-4 ${
													errors[ACTMembershipFormConstants.state]
														? "error"
														: ""
												}`}
											>
												<Field
													type="text"
													className="form-control standard-form-group"
													name={ACTMembershipFormConstants.state}
													component="select"
													placeholder=""
													errors={errors}
													touched={touched}
													onChange={(e) =>
														setFieldValue(
															ACTMembershipFormConstants.state,
															e.target.value
														)
													}
													children={<option value={"ACT"}>ACT</option>}
												/>
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.state] &&
														errors[ACTMembershipFormConstants.state]}
												</div>
											</div>
										</div>
										<div>
											<label className="formCheckboxLabel label col-md-12 my-2 px-4 textStyle">
												Suburb
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.suburb]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.suburb}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
												/>
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[ACTMembershipFormConstants.suburb] &&
														errors[ACTMembershipFormConstants.suburb]}
												</div>
											</div>
										</div>
									</div>
									<div className="col-md-6 my-2 px-4">
										<div
											className={`${
												errors[ACTMembershipFormConstants.sameAsResidential]
													? "error"
													: ""
											}`}
										>
											<Field
												className="textStyle"
												component="div"
												name={ACTMembershipFormConstants.sameAsResidential}
											>
												<label
													className="formCheckboxLabel label"
													htmlFor="sameAsResidential"
												>
													Postal address same as Residential?*
												</label>
												<div className="flexWrapper">
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.sameAsResidential
																] === "yes"
															}
															name={
																ACTMembershipFormConstants.sameAsResidential
															}
															type="radio"
															value="yes"
															className="check-input"
															id="resYes"
														/>
														<label
															className="form-check-label"
															htmlFor="resYes"
														>
															Yes
														</label>
													</div>
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.sameAsResidential
																] === "no"
															}
															name={
																ACTMembershipFormConstants.sameAsResidential
															}
															type="radio"
															value="no"
															className="check-input"
															id="resNo"
														/>
														<label
															className="form-check-label"
															htmlFor="resNo"
														>
															No
														</label>
													</div>
												</div>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[
													ACTMembershipFormConstants.sameAsResidential
												] &&
													errors[
														ACTMembershipFormConstants.sameAsResidential
													]}
											</div>
										</div>
									</div>
									{values[ACTMembershipFormConstants.sameAsResidential] ===
										"no" && (
										<div className="fieldGrid">
											<div className="textStyle">
												<label className="formCheckboxLabel label col-md-12 my-2 px-4">
													Postal Address 1
												</label>
												<div
													className={`${
														errors[ACTMembershipFormConstants.postal1]
															? "error"
															: ""
													}`}
												>
													<FormField
														name={ACTMembershipFormConstants.postal1}
														placeholder=""
														errors={errors}
														touched={touched}
														isColumn={true}
														className="form-control standard-form-group"
														colorTheme="red"
													/>{" "}
													<div className="errorMessage col-md-12 my-2 px-4">
														{touched[
															ACTMembershipFormConstants.postal1
														] &&
															errors[
																ACTMembershipFormConstants.postal1
															]}
													</div>
												</div>
											</div>
											<div className="textStyle">
												<label className="formCheckboxLabel label col-md-12 my-2 px-4">
													Postal Address 2
												</label>
												<div
													className={`${
														errors[ACTMembershipFormConstants.postal2]
															? "error"
															: ""
													}`}
												>
													<FormField
														name={ACTMembershipFormConstants.postal2}
														placeholder=""
														errors={errors}
														touched={touched}
														isColumn={true}
														className="form-control standard-form-group"
														colorTheme="red"
													/>{" "}
													<div className="errorMessage col-md-12 my-2 px-4">
														{touched[
															ACTMembershipFormConstants.postal2
														] &&
															errors[
																ACTMembershipFormConstants.postal2
															]}
													</div>
												</div>
											</div>
											<div className="textStyle">
												<label className="formCheckboxLabel label col-md-12 my-2 px-4">
													Postal Postcode
												</label>
												<div
													className={`${
														errors[
															ACTMembershipFormConstants.postalCode
														]
															? "error"
															: ""
													}`}
												>
													<FormField
														name={ACTMembershipFormConstants.postalCode}
														placeholder=""
														errors={errors}
														touched={touched}
														isColumn={true}
														className="form-control standard-form-group"
														colorTheme="red"
													/>{" "}
													<div className="errorMessage col-md-12 my-2 px-4">
														{touched[
															ACTMembershipFormConstants.postalCode
														] &&
															errors[
																ACTMembershipFormConstants
																	.postalCode
															]}
													</div>
												</div>
											</div>
											<div className="textStyle">
												<label className="formCheckboxLabel label col-md-12 my-2 px-4">
													Postal State
												</label>
												<div
													className={`col-md-12 my-1 px-4 ${
														errors[
															ACTMembershipFormConstants.postalState
														]
															? "error"
															: ""
													}`}
												>
													<Field
														type="text"
														className="form-control standard-form-group"
														name={
															ACTMembershipFormConstants.postalState
														}
														component="select"
														placeholder=""
														errors={errors}
														touched={touched}
														onChange={(e) =>
															setFieldValue(
																ACTMembershipFormConstants.postalState,
																e.target.value
															)
														}
														children={States.map((opt, index) => (
															<option key={index} value={opt.value}>
																{opt.text}
															</option>
														))}
													/>
													<div className="errorMessage col-md-12 my-2 px-4">
														{touched[
															ACTMembershipFormConstants.postalState
														] &&
															errors[
																ACTMembershipFormConstants
																	.postalState
															]}
													</div>
												</div>
											</div>
											<div className="textStyle">
												<label className="formCheckboxLabel label col-md-12 my-2 px-4">
													Postal Suburb
												</label>
												<div
													className={`${
														errors[
															ACTMembershipFormConstants.postalSuburb
														]
															? "error"
															: ""
													}`}
												>
													<FormField
														name={
															ACTMembershipFormConstants.postalSuburb
														}
														placeholder=""
														errors={errors}
														touched={touched}
														isColumn={true}
														className="form-control standard-form-group"
														colorTheme="red"
													/>{" "}
													<div className="errorMessage col-md-12 my-2 px-4">
														{touched[
															ACTMembershipFormConstants.postalSuburb
														] &&
															errors[
																ACTMembershipFormConstants
																	.postalSuburb
															]}
													</div>
												</div>
											</div>
										</div>
									)}
									<div className="col-md-12 my-2 px-4">
										<div
											className={`${
												errors[ACTMembershipFormConstants.gender]
													? "error"
													: ""
											}`}
										>
											<Field
												className="textStyle"
												component="div"
												name={ACTMembershipFormConstants.gender}
											>
												<label
													className="formCheckboxLabel label "
													htmlFor="gender"
												>
													Gender*
												</label>
												<div className="flexWrapper">
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.gender
																] === "female"
															}
															name={ACTMembershipFormConstants.gender}
															type="radio"
															value="female"
															className="check-input"
															id="resfemale"
														/>
														<label
															className="form-check-label"
															htmlFor="resfemale"
														>
															Female
														</label>
													</div>
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.gender
																] === "male"
															}
															name={ACTMembershipFormConstants.gender}
															type="radio"
															value="male"
															className="check-input"
															id="resmale"
														/>
														<label
															className="form-check-label"
															htmlFor="resmale"
														>
															Male
														</label>
													</div>
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.gender
																] === "x"
															}
															name={ACTMembershipFormConstants.gender}
															type="radio"
															value="x"
															className="check-input"
															id="resx"
														/>
														<label
															className="form-check-label"
															htmlFor="resx"
														>
															X (Indeterminate / Intersex/
															Unspecified)
														</label>
													</div>
												</div>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[ACTMembershipFormConstants.gender] &&
													errors[ACTMembershipFormConstants.gender]}
											</div>
										</div>
									</div>
									<div className="col-md-12 my-2 px-4">
										<div
											className={`${
												errors[ACTMembershipFormConstants.LBGTIQ]
													? "error"
													: ""
											}`}
										>
											<Field
												className="textStyle"
												component="div"
												name={ACTMembershipFormConstants.LBGTIQ}
											>
												<label
													className="formCheckboxLabel label"
													htmlFor="LBGTIQ"
												>
													Do you identify as LGBTIQ?
												</label>
												<div className="flexWrapper">
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.LBGTIQ
																] === "yes"
															}
															name={ACTMembershipFormConstants.LBGTIQ}
															type="radio"
															value="yes"
															className="check-input"
															id="lqbtiqYes"
														/>
														<label
															className="form-check-label"
															htmlFor="lqbtiqYes"
														>
															Yes
														</label>
													</div>
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.LBGTIQ
																] === "no"
															}
															name={ACTMembershipFormConstants.LBGTIQ}
															type="radio"
															value="no"
															className="check-input"
															id="lqbtiqNo"
														/>
														<label
															className="form-check-label"
															htmlFor="lqbtiqNo"
														>
															No
														</label>
													</div>
												</div>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[ACTMembershipFormConstants.LBGTIQ] &&
													errors[ACTMembershipFormConstants.LBGTIQ]}
											</div>
										</div>
										<div
											className={`${
												errors[ACTMembershipFormConstants.aboriginalDescent]
													? "error"
													: ""
											}`}
										>
											<Field
												className="textStyle"
												component="div"
												name={ACTMembershipFormConstants.aboriginalDescent}
											>
												<label
													className="formCheckboxLabel label"
													htmlFor="aboriginalDescent"
												>
													Do you identify as Aboriginal or Torres Strait
													Islander?*
												</label>
												<div className="flexWrapper">
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.aboriginalDescent
																] === "yes"
															}
															name={
																ACTMembershipFormConstants.aboriginalDescent
															}
															type="radio"
															value="yes"
															className="check-input"
															id="descentYes"
														/>
														<label
															className="form-check-label"
															htmlFor="descentYes"
														>
															Yes
														</label>
													</div>
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.aboriginalDescent
																] === "no"
															}
															name={
																ACTMembershipFormConstants.aboriginalDescent
															}
															type="radio"
															value="no"
															className="check-input"
															id="descentNo"
														/>
														<label
															className="form-check-label"
															htmlFor="descentNo"
														>
															No
														</label>
													</div>
												</div>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[
													ACTMembershipFormConstants.aboriginalDescent
												] &&
													errors[
														ACTMembershipFormConstants.aboriginalDescent
													]}
											</div>
										</div>
										<div
											className={`${
												errors[ACTMembershipFormConstants.CALD]
													? "error"
													: ""
											}`}
										>
											<Field
												className="textStyle"
												component="div"
												name={ACTMembershipFormConstants.CALD}
											>
												<label
													className="formCheckboxLabel label"
													htmlFor="CALD"
												>
													Do you identify as Culturally and Linguistically
													Diverse?
												</label>
												<div className="flexWrapper">
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants.CALD
																] === "yes"
															}
															name={ACTMembershipFormConstants.CALD}
															type="radio"
															value="yes"
															className="check-input"
															id="caldYes"
														/>
														<label
															className="form-check-label"
															htmlFor="caldYes"
														>
															Yes
														</label>
													</div>
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants.CALD
																] === "no"
															}
															name={ACTMembershipFormConstants.CALD}
															type="radio"
															value="no"
															className="check-input"
															id="caldNo"
														/>
														<label
															className="form-check-label"
															htmlFor="caldNo"
														>
															No
														</label>
													</div>
												</div>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[ACTMembershipFormConstants.CALD] &&
													errors[ACTMembershipFormConstants.CALD]}
											</div>
										</div>
									</div>
									<div className="fieldGrid">
										<div className="textStyle">
											<label className="formCheckboxLabel label col-md-12 my-2 px-4">
												Ethnicity
											</label>
											<div
												className={`col-md-12 my-1 px-4 ${
													errors[ACTMembershipFormConstants.ethnicity]
														? "error"
														: ""
												}`}
											>
												<Field
													type="text"
													className="form-control standard-form-group"
													name={ACTMembershipFormConstants.ethnicity}
													component="select"
													placeholder=""
													errors={errors}
													touched={touched}
													onChange={(e) =>
														setFieldValue(
															ACTMembershipFormConstants.ethnicity,
															e.target.value
														)
													}
													children={Ethnicities.map((opt, index) => (
														<option key={index} value={opt}>
															{opt}
														</option>
													))}
												/>
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants.ethnicity
													] &&
														errors[
															ACTMembershipFormConstants.ethnicity
														]}
												</div>
											</div>
										</div>
										<div className="textStyle">
											<label className="formCheckboxLabel label col-md-12 my-2 px-4">
												Primary Language Spoken
											</label>
											<div
												className={`col-md-12 my-1 px-4 ${
													errors[
														ACTMembershipFormConstants.primaryLanguage
													]
														? "error"
														: ""
												}`}
											>
												<Field
													type="text"
													className="form-control standard-form-group"
													name={
														ACTMembershipFormConstants.primaryLanguage
													}
													component="select"
													placeholder=""
													errors={errors}
													touched={touched}
													onChange={(e) =>
														setFieldValue(
															ACTMembershipFormConstants.primaryLanguage,
															e.target.value
														)
													}
													children={Languages.map((opt, index) => (
														<option key={index} value={opt}>
															{opt}
														</option>
													))}
												/>
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants.primaryLanguage
													] &&
														errors[
															ACTMembershipFormConstants
																.primaryLanguage
														]}
												</div>
											</div>
										</div>
									</div>
									<div className="col-md-12 my-2 px-4">
										<div
											className={`${
												errors[ACTMembershipFormConstants.hasDisability]
													? "error"
													: ""
											}`}
										>
											<Field
												className="textStyle"
												component="div"
												name={ACTMembershipFormConstants.hasDisability}
											>
												<label
													className="formCheckboxLabel label "
													htmlFor="hasDisablity"
												>
													Do you identify as a person with a disability?
												</label>
												<div className="flexWrapper">
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.hasDisability
																] === "yes"
															}
															name={
																ACTMembershipFormConstants.hasDisability
															}
															type="radio"
															value="yes"
															className="check-input"
															id="disabledYes"
														/>
														<label
															className="form-check-label"
															htmlFor="disabledYes"
														>
															Yes
														</label>
													</div>
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.hasDisability
																] === "no"
															}
															name={
																ACTMembershipFormConstants.hasDisability
															}
															type="radio"
															value="no"
															className="check-input"
															id="disabledNo"
														/>
														<label
															className="form-check-label"
															htmlFor="disabledNo"
														>
															No
														</label>
													</div>
												</div>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[
													ACTMembershipFormConstants.hasDisability
												] &&
													errors[
														ACTMembershipFormConstants.hasDisability
													]}
											</div>
										</div>
									</div>
									{values[ACTMembershipFormConstants.hasDisability] === "yes" && (
										<>
											<label className="col-md-12 my-2 px-4 formCheckboxLabel label textStyle">
												ACT Labor works hard to create an inclusive
												environment for party members. If you identify as
												having a disability that you would like ACT Labor to
												be aware of please let us know the details below.
											</label>
											<div
												className={`${
													errors[ACTMembershipFormConstants.disability]
														? "error"
														: ""
												}`}
											>
												<FormField
													name={ACTMembershipFormConstants.disability}
													placeholder=""
													errors={errors}
													touched={touched}
													isColumn={true}
													className="form-control standard-form-group"
													colorTheme="red"
													component="textarea"
												/>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants.disability
													] &&
														errors[
															ACTMembershipFormConstants.disability
														]}
												</div>
											</div>
										</>
									)}
									<h1 className="donFormHeader label">2.Membership</h1>
									<div className="col-md-12 my-2 px-4">
										<div
											className={`${
												errors[ACTMembershipFormConstants.transferring]
													? "error"
													: ""
											}`}
										>
											<Field
												className="textStyle"
												component="div"
												name={ACTMembershipFormConstants.transferring}
											>
												<label
													className="formCheckboxLabel label "
													htmlFor="transferring"
												>
													Are you transferring your membership from
													another state or territory?
												</label>
												<div className="flexWrapper">
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.transferring
																] === "yes"
															}
															name={
																ACTMembershipFormConstants.transferring
															}
															type="radio"
															value="yes"
															className="check-input"
															id="transferYes"
														/>
														<label
															className="form-check-label"
															htmlFor="transferYes"
														>
															Yes
														</label>
													</div>
													<div className="radioWrapper form-check">
														<input
															defaultChecked={
																values[
																	ACTMembershipFormConstants
																		.transferring
																] === "no"
															}
															name={
																ACTMembershipFormConstants.transferring
															}
															type="radio"
															value="no"
															className="check-input"
															id="transferNo"
														/>
														<label
															className="form-check-label"
															htmlFor="transferNo"
														>
															No
														</label>
													</div>
												</div>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[ACTMembershipFormConstants.transferring] &&
													errors[ACTMembershipFormConstants.transferring]}
											</div>
										</div>
									</div>
									{values[ACTMembershipFormConstants.transferring] === "yes" && (
										<div className="fieldGrid">
											<div>
												<label className="formCheckboxLabel col-md-12 my-2 px-4 label textStyle">
													State
												</label>
												<div
													className={`col-md-12 my-1 px-4 ${
														errors[
															ACTMembershipFormConstants
																.transferringFrom
														]
															? "error"
															: ""
													}`}
												>
													<Field
														type="text"
														className="form-control standard-form-group"
														name={
															ACTMembershipFormConstants.transferringFrom
														}
														component="select"
														placeholder=""
														errors={errors}
														touched={touched}
														onChange={(e) =>
															setFieldValue(
																ACTMembershipFormConstants.transferringFrom,
																e.target.value
															)
														}
														children={States.filter(
															(st) => st.value !== "ACT"
														).map((opt, index) => (
															<option key={index} value={opt.value}>
																{opt.text}
															</option>
														))}
													/>
													<div className="errorMessage col-md-12 my-2 px-4">
														{touched[
															ACTMembershipFormConstants
																.transferringFrom
														] &&
															errors[
																ACTMembershipFormConstants
																	.transferringFrom
															]}
													</div>
												</div>
											</div>
											<div>
												<label className="formCheckboxLabel col-md-12 my-2 px-4 label textStyle">
													What was your previous membership number?
												</label>
												<div
													className={`${
														errors[
															ACTMembershipFormConstants
																.previousMembershipNumber
														]
															? "error"
															: ""
													}`}
												>
													<FormField
														name={
															ACTMembershipFormConstants.previousMembershipNumber
														}
														placeholder=""
														errors={errors}
														touched={touched}
														isColumn={true}
														className="form-control standard-form-group"
														colorTheme="red"
													/>{" "}
													<div className="errorMessage col-md-12 my-2 px-4">
														{touched[
															ACTMembershipFormConstants
																.previousMembershipNumber
														] &&
															errors[
																ACTMembershipFormConstants
																	.previousMembershipNumber
															]}
													</div>
												</div>
											</div>
										</div>
									)}
									<br />
									<label className="formCheckboxLabel textStyle col-md-12 my-2 px-4 label">
										Which of the following categories best describes your
										circumstance? You may only select one.*
									</label>
									<div className="col-md-12 my-2 px-4">
										<div
											className={`${
												errors[ACTMembershipFormConstants.category]
													? "error"
													: ""
											}`}
										>
											<Field
												className="textStyle"
												component="div"
												name={ACTMembershipFormConstants.category}
											>
												<label className="formCheckboxLabel textStyle label">{`Concession ($${formatCurrency(
													props.concessionCardDiscountAmount / 100
												)}/year)`}</label>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "under18"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="under18"
														className="check-input"
														id="resunder18"
													/>
													<label
														className="form-check-label"
														htmlFor="resunder18"
													>
														under 18 years of age
													</label>
												</div>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "conces"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="conces"
														className="check-input"
														id="respension"
													/>
													<label
														className="form-check-label"
														htmlFor="respension"
													>
														government pension or payment card
													</label>
												</div>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "unemp"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="unemp"
														className="check-input"
														id="resunemployed"
													/>
													<label
														className="form-check-label"
														htmlFor="resunemployed"
													>
														unemployed
													</label>
												</div>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "retrd"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="retrd"
														className="check-input"
														id="resretired"
													/>
													<label
														className="form-check-label"
														htmlFor="resretired"
													>
														retired from work
													</label>
												</div>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "student"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="student"
														className="check-input"
														id="resstudent"
													/>
													<label
														className="form-check-label"
														htmlFor="resstudent"
													>
														study full-time
													</label>
												</div>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "casual"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="casual"
														className="check-input"
														id="rescasual"
													/>
													<label
														className="form-check-label"
														htmlFor="rescasual"
													>
														casually employed
													</label>
												</div>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "norfolk"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="norfolk"
														className="check-input"
														id="resnorfolk"
													/>
													<label
														className="form-check-label"
														htmlFor="resnorfolk"
													>
														enrolled in Norfolk Island
													</label>
												</div>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "os"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="os"
														className="check-input"
														id="resoverseas"
													/>
													<label
														className="form-check-label"
														htmlFor="resoverseas"
													>
														on an overseas posting or mission
													</label>
												</div>
												<label
													style={{ marginTop: "8px" }}
													className="formCheckboxLabel textStyle label"
												>{`Discount ($${formatCurrency(
													props.unionDiscountAmount / 100
												)}/month)`}</label>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "union"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="union"
														className="check-input"
														id="resunion"
													/>
													<label
														className="form-check-label"
														htmlFor="resunion"
													>
														union member
													</label>
												</div>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "pt"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="pt"
														className="check-input"
														id="resparttime"
													/>
													<label
														className="form-check-label"
														htmlFor="resparttime"
													>
														part-time worker
													</label>
												</div>
												<label
													style={{ marginTop: "8px" }}
													className="formCheckboxLabel textStyle label"
												>{`General ($${formatCurrency(
													props.yearlyPaymentAmount / 100
												)}/month)`}</label>
												<div className="radioWrapper form-check">
													<input
														defaultChecked={
															values[
																ACTMembershipFormConstants.category
															] === "gen"
														}
														name={ACTMembershipFormConstants.category}
														type="radio"
														value="gen"
														className="check-input"
														id="resnone"
													/>
													<label
														className="form-check-label"
														htmlFor="resnone"
													>
														none of the above or full-time worker
													</label>
												</div>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[ACTMembershipFormConstants.category] &&
													errors[ACTMembershipFormConstants.category]}
											</div>
										</div>
									</div>
									<h1 className="donFormHeader label">
										3.Occupation and Union Membership
									</h1>
									<label className="col-md-12 my-2 px-4 formCheckboxLabel label textStyle">
										Occupation*
									</label>
									<div
										className={`${
											errors[ACTMembershipFormConstants.occupation]
												? "error"
												: ""
										}`}
									>
										<FormField
											name={ACTMembershipFormConstants.occupation}
											placeholder=""
											errors={errors}
											touched={touched}
											isColumn={true}
											className="form-control standard-form-group"
											colorTheme="red"
										/>{" "}
										<div className="errorMessage col-md-12 my-2 px-4">
											{touched[ACTMembershipFormConstants.occupation] &&
												errors[ACTMembershipFormConstants.occupation]}
										</div>
									</div>
									<div className="fieldGrid">
										<div className="col-md-12 my-2 px-4">
											<div
												className={`${
													errors[ACTMembershipFormConstants.isUnionMember]
														? "error"
														: ""
												}`}
											>
												<Field
													className="textStyle col-md-12 my-1 px-4"
													component="div"
													name={ACTMembershipFormConstants.isUnionMember}
												>
													<label
														className="formCheckboxLabel label "
														htmlFor="union"
													>
														Are you a member of a trade union?*
													</label>
													<div className="flexWrapper">
														<div className="radioWrapper form-check">
															<input
																defaultChecked={
																	values[
																		ACTMembershipFormConstants
																			.isUnionMember
																	] === "yes"
																}
																name={
																	ACTMembershipFormConstants.isUnionMember
																}
																type="radio"
																value="yes"
																className="check-input"
																id="unionYes"
															/>
															<label
																className="form-check-label"
																htmlFor="unionYes"
															>
																Yes
															</label>
														</div>
														<div className="radioWrapper form-check">
															<input
																defaultChecked={
																	values[
																		ACTMembershipFormConstants
																			.isUnionMember
																	] === "no"
																}
																name={
																	ACTMembershipFormConstants.isUnionMember
																}
																type="radio"
																value="no"
																className="check-input"
																id="unionNo"
															/>
															<label
																className="form-check-label"
																htmlFor="unionNo"
															>
																No
															</label>
														</div>
													</div>
												</Field>{" "}
												<div className="errorMessage col-md-12 my-2 px-4">
													{touched[
														ACTMembershipFormConstants.isUnionMember
													] &&
														errors[
															ACTMembershipFormConstants.isUnionMember
														]}
												</div>
											</div>
										</div>
										{values[ACTMembershipFormConstants.isUnionMember] ===
											"yes" && (
											<div>
												<label className="formCheckboxLabel col-md-12 my-2 px-4 textStyle label">
													Which union are you a member of?*
												</label>
												<div
													className={`col-md-12 my-1 px-4 ${
														errors[
															ACTMembershipFormConstants.unionMemberOf
														]
															? "error"
															: ""
													}`}
												>
													<Field
														type="text"
														className="form-control standard-form-group"
														name={
															ACTMembershipFormConstants.unionMemberOf
														}
														component="select"
														placeholder=""
														errors={errors}
														touched={touched}
														onChange={(e) =>
															setFieldValue(
																ACTMembershipFormConstants.unionMemberOf,
																e.target.value
															)
														}
														children={props?.unionList.map(
															(opt, index) => (
																<option
																	key={index}
																	value={opt.value}
																>
																	{opt.text}
																</option>
															)
														)}
													/>
													<div className="errorMessage col-md-12 my-2 px-4">
														{touched[
															ACTMembershipFormConstants.unionMemberOf
														] &&
															errors[
																ACTMembershipFormConstants
																	.unionMemberOf
															]}
													</div>
												</div>
											</div>
										)}
									</div>
									<h1 className="donFormHeader label">4.Declarations</h1>
									<div
										className={`col-md-12 my-2 px-4 formCheckboxWrapper ${
											errors["declareContactShared"] &&
											touched["declareContactShared"]
												? " "
												: ""
										}
                                        `}
									>
										<div
											className={`${
												errors[
													ACTMembershipFormConstants.declareContactShared
												]
													? "error"
													: ""
											}`}
										>
											<Field
												className={"formCheckBox"}
												component="div"
												name={
													ACTMembershipFormConstants.declareContactShared
												}
											>
												<input
													defaultChecked={
														values[
															ACTMembershipFormConstants
																.declareContactShared
														] === true
													}
													name={
														ACTMembershipFormConstants.declareContactShared
													}
													type="checkbox"
													value="yes"
													id="declareContactShared"
													className="formRadioTick"
												/>
												<label
													className="formCheckboxLabel label textStyle"
													htmlFor="declareContactShared"
												>
													I acknowledge that my contact information may be
													shared with ALP members, branches and affiliated
													unions.*
												</label>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[
													ACTMembershipFormConstants.declareContactShared
												] &&
													errors[
														ACTMembershipFormConstants
															.declareContactShared
													]}
											</div>
										</div>
									</div>
									<div
										className={`col-md-12 my-2 px-4 formCheckboxWrapper ${
											errors["declareNotPoliticalMember"] &&
											touched["declareNotPoliticalMember"]
												? ""
												: ""
										}
                                        `}
									>
										<div
											className={`${
												errors[
													ACTMembershipFormConstants
														.declareNotPoliticalMember
												]
													? "error"
													: ""
											}`}
										>
											<Field
												className={"formCheckBox"}
												component="div"
												name={
													ACTMembershipFormConstants.declareNotPoliticalMember
												}
											>
												<input
													defaultChecked={
														values[
															ACTMembershipFormConstants
																.declareNotPoliticalMember
														] === true
													}
													name={
														ACTMembershipFormConstants.declareNotPoliticalMember
													}
													type="checkbox"
													value="yes"
													id="declareNotPoliticalMember"
													className="formRadioTick"
												/>
												<label
													className="textStyle label formCheckboxLabel"
													htmlFor="declareNotPoliticalMember"
												>
													I declare I am not a member of any other
													political party (unless it is a fellow party of
													the Socialist International/Progressive
													Alliance).*
												</label>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[
													ACTMembershipFormConstants
														.declareNotPoliticalMember
												] &&
													errors[
														ACTMembershipFormConstants
															.declareNotPoliticalMember
													]}
											</div>
										</div>
									</div>
									<div
										className={`col-md-12 my-2 px-4 formCheckboxWrapper ${
											errors["declareJoinAlp"] && touched["declareJoinAlp"]
												? ""
												: ""
										}
                                        `}
									>
										<div
											className={`${
												errors[ACTMembershipFormConstants.declareJoinAlp]
													? "error"
													: ""
											}`}
										>
											<Field
												className={"formCheckBox"}
												component="div"
												name={ACTMembershipFormConstants.declareJoinAlp}
											>
												<input
													defaultChecked={
														values[
															ACTMembershipFormConstants
																.declareJoinAlp
														] === true
													}
													name={ACTMembershipFormConstants.declareJoinAlp}
													type="checkbox"
													value="yes"
													id="declareJoinAlp"
													className="formRadioTick"
												/>
												<label
													className="formCheckboxLabel label textStyle"
													htmlFor="declareJoinAlp"
												>
													I hereby apply for membership of the Australian
													Labor Party (ACT Branch). I declare that the
													details in this application are true and
													correct. If I am accepted as a member I hereby
													undertake to support the platform of the
													Australian Labor Party, to vote for and assist
													the return of endorsed Australian Labor Party
													candidates and to abide by the constitution and
													rules of the Australian Labor Party.*
												</label>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[
													ACTMembershipFormConstants.declareJoinAlp
												] &&
													errors[
														ACTMembershipFormConstants.declareJoinAlp
													]}
											</div>
										</div>
									</div>
									<div
										className={`col-md-12 my-2 px-4 formCheckboxWrapper ${
											errors["declarePayAlp"] && touched["declarePayAlp"]
												? ""
												: ""
										}
                                        `}
									>
										<div
											className={`${
												errors[ACTMembershipFormConstants.declarePayAlp]
													? "error"
													: ""
											}`}
										>
											<Field
												className={"formCheckBox"}
												component="div"
												name={ACTMembershipFormConstants.declarePayAlp}
											>
												<input
													defaultChecked={
														values[
															ACTMembershipFormConstants.declarePayAlp
														] === true
													}
													name={ACTMembershipFormConstants.declarePayAlp}
													type="checkbox"
													value="yes"
													id="declarePayAlp"
													className="formRadioTick"
												/>
												<label
													className="formCheckboxLabel label textStyle"
													htmlFor="declarePayAlp"
												>
													I authorise ACT Labor to deduct the prescribed
													amount according to the frequency defined by my
													membership category using the payment method you
													select.*
												</label>
											</Field>{" "}
											<div className="errorMessage col-md-12 my-2 px-4">
												{touched[
													ACTMembershipFormConstants.declarePayAlp
												] &&
													errors[
														ACTMembershipFormConstants.declarePayAlp
													]}
											</div>
										</div>
									</div>
									{Object.entries(ACTMembershipFormConstants).some(
										([key, value]) => errors[ACTMembershipFormConstants[key]]
									) && (
										<div className="errorMessage submitError col-md-12 my-2 px-4">
											Please fill in all required fields
										</div>
									)}
									<button
										type="submit"
										className="donButton redContSubButton"
										onClick={() => {
											if (continueClickHandler(errors, setFieldTouched))
												setModalVisible(true);
										}}
									>
										Next
									</button>
								</>
							)}
						</div>
						<h3 className="errorMessage submitError col-md-12 my-2 px-4">
							{submitError}
						</h3>
						{success && (
							<div className="submitError col-md-12 my-2 px-4">
								<h2>Thank you for joining ACT Labor</h2>
								<p>A confirmation email will be sent to you</p>
							</div>
						)}
					</LoadingOverlay>
					<Modal show={modalVisible} size={"xl"}>
						<Modal.Header closeButton>
							<Modal.Title>{`Membership type: ${
								categoryMap[values[ACTMembershipFormConstants.category]]
							}`}</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<h3 className="label textStyle">{`Total payment: $${formatCurrency(
								getCategoryDetails(values[ACTMembershipFormConstants.category])
									?.amount / 100
							)} (${
								getCategoryDetails(values[ACTMembershipFormConstants.category])
									?.interval
							})`}</h3>
							<div>
								Membership will be automatically renewed. You can cancel anytime.
							</div>
							<StripeCheckout
								setComplete={setStripeComplete}
								setSubmitError={setSubmitError}
								contentId={props.contentId}
								stripe={props.stripe}
								values={values}
							/>
						</Modal.Body>
						<Modal.Footer>
							<Button
								variant="primary"
								onClick={() => submitFunction(values)}
								disabled={!stripeComplete || submitting}
							>
								Ok
							</Button>
							<Button variant="primary" onClick={() => setModalVisible(false)}>
								Cancel
							</Button>
						</Modal.Footer>
					</Modal>
				</>
			)}
		/>
	);
}
export default injectStripe(ACTMembershipForm);
